import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ErrorHandler, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, metadata, min, PathKind, required, REQUIRED, SchemaPath, SchemaPathRules, validate } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidators } from 'app/_jaettu-angular/_validators/FormValidators'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { KycUriService } from '@lemon/core-firebase/service/kyc-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from 'app/_jaettu-angular/service/maa.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'

import { CustomerBeneficiary, CustomerHallituksenJasen, CustomerOsakas, KnowYourCustomer, KnowYourCustomerSeenLog, Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { TilinpaatosUserConversionWorkQueue } from 'app/_jaettu-lemonator/model/tilinpaatos'
import { BehaviorSubject, combineLatest, distinctUntilKeyChanged, map, Observable, of as observableOf, startWith, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs'
import { Asiakas } from '../../../../_jaettu-lemonator/model/asiakas'

import { StringService } from '@lemon/core/service/string.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'

interface OsakasRivi {
  nimi: string
  hetu: string
  osakkeita: number | null
  kansallisuus: string | null
  pep: boolean | null
  noFinnishId: boolean | null
  onkoYritys: boolean | null
  birthDate: Date | null
}

interface HallituksenJasenRivi {
  nimi: string
  hetu: string
  kansallisuus: string | null
  pep: boolean | null
  noFinnishId: boolean | null
  birthDate: Date | null
  rooli: CustomerHallituksenJasen['rooli'] | null
}

interface EdunsaajaRivi {
  nimi: string
  hetu: string
  kansallisuus: string | null
  pep: boolean | null
  noFinnishId: boolean | null
  birthDate: Date | null
}

interface TunnistamistiedotLomake {
  osakkaat: OsakasRivi[]
  hallitus: HallituksenJasenRivi[]
  edunsaajat: EdunsaajaRivi[]
}

/** Kuten reaktiivinen Validators.required: false kelpaa arvoksi, toisin kuin Signal Formsin required-säännössä. */
function pakollinenTotuusarvo<TPathKind extends PathKind = PathKind.Root>(polku: SchemaPath<boolean | null, SchemaPathRules.Supported, TPathKind>) {
  metadata(polku, REQUIRED, () => true)
  validoiReaktiivisesti(polku, Validators.required)
}

function ilmoitaUudelleen<T>(kentta: FieldTree<T>) {
  asetaArvo(kentta, kentta().value())
}

interface KycLastSeenTexts {
  kayttajanNimi: string
  /** Converted from timestamp to string */
  seenAtString: string
}
@Component({
  selector: 'app-osakkaat-hallitus-edunsaajat',
  templateUrl: './osakkaat-hallitus-edunsaajat.component.html',
  styleUrls: ['../tuntemistiedot.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AsiakasAsetuksetOsakkaatHallitusEdunsaajatComponent implements OnInit, OnDestroy {
  @Input() set asiakas(asiakas: Asiakas) {
    this._asiakasSubject.next(asiakas)
  }

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _lomakePoissa = signal(false)
  private readonly _osakkaatPoissa = signal(false)
  private readonly _hallitusPoissa = signal(false)
  private readonly _edunsaajatPoissa = signal(false)
  private readonly _hallitukseltaVaaditaanJasen = signal(true)
  private readonly _rivienTilaukset: { osakkaat: Subscription[]; hallitus: Subscription[]; edunsaajat: Subscription[] } = { osakkaat: [], hallitus: [], edunsaajat: [] }

  lomake = form(signal<TunnistamistiedotLomake>({ osakkaat: [], hallitus: [], edunsaajat: [] }), polku => {
    disabled(polku, () => this._lomakePoissa())
    disabled(polku.osakkaat, () => this._osakkaatPoissa())
    disabled(polku.hallitus, () => this._hallitusPoissa())
    disabled(polku.edunsaajat, () => this._edunsaajatPoissa())
    validate(polku.osakkaat, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
    validate(polku.hallitus, ({ value }) => (this._hallitukseltaVaaditaanJasen() && value().length < 1 ? { kind: 'min' } : null))
    applyEach(polku.osakkaat, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, FormValidators.finnishHetuYtunnusOrVatNumberValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.osakkeita)
      min(rivi.osakkeita, 1)
      required(rivi.kansallisuus)
      pakollinenTotuusarvo(rivi.pep)
      pakollinenTotuusarvo(rivi.onkoYritys)
      disabled(rivi.onkoYritys, ({ valueOf }) => !valueOf(rivi.noFinnishId))
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId) || !!valueOf(rivi.onkoYritys))
    })
    applyEach(polku.hallitus, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, FormValidators.finnishPersonalIdValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.kansallisuus)
      pakollinenTotuusarvo(rivi.pep)
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId))
      required(rivi.rooli)
    })
    applyEach(polku.edunsaajat, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, FormValidators.finnishPersonalIdValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.kansallisuus)
      pakollinenTotuusarvo(rivi.pep)
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId))
    })
  })
  commonError: string
  namename = 'asdf ' + Math.random()

  onkoTmi: boolean

  selectedHistorySubject: BehaviorSubject<KnowYourCustomer> = new BehaviorSubject(null)
  deletedOsakkaatText: string
  deletedHallitusText: string
  deletedEdunsaajatText: string

  private tunnistamistiedot: KnowYourCustomer = {
    asiakasAvain: null,
    avain: null,
    edunsaajat: [],
    hallitus: [],
    luoja: null,
    luotu: null,
    osakkaat: [],
    paivitetty: null,
    paivittaja: null,
    poistettu: false,
  }

  private _asiakasSubject: BehaviorSubject<Asiakas> = new BehaviorSubject(null)
  asiakasObservable: Observable<Asiakas> = this._asiakasSubject.asObservable().pipe(distinctUntilKeyChanged('avain'))
  knowYourCustomerObservable: Observable<KnowYourCustomer>
  knowYourCustomerLastUpdatedObservable: Observable<string>
  kansallisuudetObservable: Observable<Maa[]>
  hallitusRoolitObservable: Observable<{ avain: Pick<CustomerHallituksenJasen, 'rooli'>; nimi: string }[]>
  kycLastSeenLogObservable: Observable<KycLastSeenTexts>
  osakkeidenYhteisMaaraObservable: Observable<number>

  isBrowsingHistory: boolean
  prevActive: boolean
  nextActive: boolean
  versioLuotu: string
  historyChangesMap: Map<string, string> = new Map()

  minDate = new Date(1900, 1, 1)
  maxDate = new Date(2100, 1, 1)

  @Output() tuntemistiedotAntamatta: EventEmitter<boolean> = new EventEmitter()

  constructor(
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _copyService: KopioijaPalvelu,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _translationService: LemonTranslationService,
    private _timestampService: TimestampService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _errorHandler: ErrorHandler,
    private _maaService: MaaService,
    private _dialog: MatDialog,
    private _asiakasService: AsiakasService,
    private _validationService: FormValidationService,
    private _dateService: DateService,
    private _kycUriService: KycUriService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _stringService: StringService,
  ) {}

  ngOnInit() {
    this.knowYourCustomerObservable = this.asiakasObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          if (asiakas.yritysmuoto === Yritysmuoto.TOIMINIMI) {
            this.tuntemistiedotAntamatta.emit(false) // Tuntemistiedot not needed for tmi
            return observableOf<KnowYourCustomer>(null)
          }
          if (asiakas.yritysmuoto === Yritysmuoto.AVOINYHTIO || asiakas.yritysmuoto === Yritysmuoto.KOMMANDIITTIYHTIO) {
            this._hallitukseltaVaaditaanJasen.set(false)
          }

          return this._firebaseLemonaid
            .firestoreDoc<KnowYourCustomer>(this._kycUriService.getCustomerKycDocUri(asiakas.avain))
            .listen()
            .pipe(
              tap(kyc => {
                if (asiakas && asiakas.yritysmuoto !== Yritysmuoto.TOIMINIMI && asiakas.yritysmuoto !== Yritysmuoto.AVOINYHTIO && !kyc?.osakkaat?.length) {
                  this.tuntemistiedotAntamatta.emit(true)
                } else {
                  this.tuntemistiedotAntamatta.emit(false)
                }
              }),
              map(kyc => {
                if (kyc) {
                  return kyc
                }
                const blankKyc: KnowYourCustomer = this._createBlankKyc()
                return blankKyc
              }),
            )
        }
        return observableOf(null)
      }),
    )

    this.knowYourCustomerLastUpdatedObservable = combineLatest([
      this.knowYourCustomerObservable,
      this._asiakasService.nykyisenAsiakkaanKayttajatObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
    ]).pipe(
      map(([data, kayttajat, kirjanpitajienNimitiedotMap]) => {
        if (data?.paivitetty && data?.paivittaja && kayttajat) {
          let kayttajanNimi = null
          const kp = kirjanpitajienNimitiedotMap.get(data.paivittaja)
          if (kp) {
            kayttajanNimi = kp.etunimi + ' ' + kp.sukunimi + ' (kirjanpitäjä)'
          } else {
            const kayttaja = kayttajat.find(k => k.avain === data.paivittaja)
            kayttajanNimi = kayttaja ? kayttaja.etunimi + ' ' + kayttaja.sukunimi : 'Tuntematon käyttäjä'
          }
          const aikaleima = this._dateService.muotoilePaivaJaAikaDate(this._dateService.timestampToDate(data.paivitetty), 'fi')
          return kayttajanNimi + ' ' + aikaleima
        }
        return ''
      }),
    )

    this.kycLastSeenLogObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._asiakasService.nykyisenAsiakkaanKayttajatObservable]).pipe(
      switchMap(([asiakas, kayttajat]) => {
        if (!asiakas?.avain) {
          return observableOf<KycLastSeenTexts>(null)
        }
        return this._firebaseLemonaid
          .firestoreDoc<KnowYourCustomerSeenLog>(this._kycUriService.getCustomerKycSeenLogDocUri(asiakas.avain))
          .listen()
          .pipe(
            map(kycSeenLog => {
              if (!kycSeenLog) {
                return null
              }
              const kayttaja = kayttajat?.find(k => k.avain === kycSeenLog.kayttajaAvain)
              const kycLastSeenTexts: KycLastSeenTexts = {
                kayttajanNimi: kayttaja ? kayttaja.etunimi + ' ' + kayttaja.sukunimi : 'Tuntematon käyttäjä',
                seenAtString: this._dateService.muotoilePaivaJaAikaDate(kycSeenLog.seenAt.toDate(), 'fi'),
              }
              return kycLastSeenTexts
            }),
          )
      }),
    )

    this.kansallisuudetObservable = this._maaService.kaikkiMaatObservable.pipe(
      map(kaikkiMaat => {
        const maatCopy = [...kaikkiMaat]
        // Make selecting a bit easier
        const suomi = kaikkiMaat.find(maa => maa.koodi === 'FIN')
        const ruotsi = kaikkiMaat.find(maa => maa.koodi === 'SWE')
        maatCopy.unshift({ nimi: '---', koodi: null })
        maatCopy.unshift(ruotsi)
        maatCopy.unshift(suomi)
        return maatCopy
      }),
    )

    this.hallitusRoolitObservable = this._translationService.currentLanguageObservable.pipe(
      takeUntil(this._ngUnsubscribe),
      map(kieli => {
        const roolit = [
          { avain: 'puheenjohtaja', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-puheenjohtaja', kieli === 'en' ? 'en' : 'fi') },
          { avain: 'jasen', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-jasen', kieli === 'en' ? 'en' : 'fi') },
          { avain: 'toimitusjohtaja', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.toimitusjohtaja', kieli === 'en' ? 'en' : 'fi') },
          { avain: 'varajasen', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-varajasen', kieli === 'en' ? 'en' : 'fi') },
        ] as { avain: Pick<CustomerHallituksenJasen, 'rooli'>; nimi: string }[]

        return roolit
      }),
    )

    combineLatest([this.knowYourCustomerObservable, this.selectedHistorySubject])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([current, history]) => {
        if (current) {
          this.tunnistamistiedot = this._copyService.cloneObjectDeep(current)

          this.nextActive = current.paivitetty?.toMillis() !== history?.paivitetty?.toMillis()

          if (this.isBrowsingHistory && history) {
            this._alustaKycLomakkeenTiedot(history)
            this._poistaLomakeKaytosta()
          } else {
            this._alustaKycLomakkeenTiedot(this.tunnistamistiedot)
          }
          this._changeDetectorRef.markForCheck()
        }
      })

    this.asiakasObservable
      .pipe(
        map(asiakas => {
          return asiakas.yritysmuoto === Yritysmuoto.TOIMINIMI
        }),
      )
      .subscribe(onkoTmi => {
        this.onkoTmi = onkoTmi

        if (onkoTmi) {
          this._osakkaatPoissa.set(true)
          this._hallitusPoissa.set(true)
          this._edunsaajatPoissa.set(true)
        }
        this.selectDisplayedHistory('init')
      })

    this.osakkeidenYhteisMaaraObservable = combineLatest([arvonMuutokset(this.lomake.osakkaat, { injector: this._injector }).pipe(startWith(null)), this.selectedHistorySubject]).pipe(
      map(() => {
        let yhteensa = 0
        for (const rivi of this.lomake.osakkaat().value()) {
          const osakkeita = rivi.osakkeita
          yhteensa += osakkeita ?? 0
        }
        return yhteensa
      }),
    )
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  // Rivin lisäys ja poisto eivät lähetä muiden rivien kenttien arvoja uudelleen, kuten FormArrayn push ja removeAt.
  private _asetaRivit<T>(taulukko: FieldTree<T[]>, rivit: T[]) {
    taulukko().value.set(rivit)
  }

  private _muutokset(tilaukset: Subscription) {
    return <T>(kentta: FieldTree<T>, kasittelija: (arvo: T) => void) => {
      tilaukset.add(arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe)).subscribe(kasittelija))
    }
  }

  lisaaUusiOsakas(firestoresta?: CustomerOsakas) {
    const osakas: CustomerOsakas = firestoresta
      ? firestoresta
      : {
          hetuTaiYtunnus: '',
          nimi: '',
          osakkeita: null,
          kansallisuus: null,
          pep: null,
          noFinnishId: null,
          birthDate: null,
          onkoYritys: null,
        }

    const rivit = this.lomake.osakkaat().value()
    this._asetaRivit(this.lomake.osakkaat, [
      ...rivit,
      {
        nimi: osakas.nimi ?? '',
        hetu: osakas.hetuTaiYtunnus ?? '',
        osakkeita: osakas.osakkeita ?? null,
        kansallisuus: osakas.kansallisuus ?? null,
        pep: osakas.pep ?? null,
        noFinnishId: false,
        onkoYritys: null,
        birthDate: osakas.birthDate ? this._dateService.localDateToDate(osakas.birthDate) : null,
      },
    ])
    this._osakkaatPoissa.set(false)
    const rivi = this.lomake.osakkaat[rivit.length]
    const tilaukset = new Subscription()
    const muutokset = this._muutokset(tilaukset)

    muutokset(rivi.nimi, value => {
      osakas.nimi = value || null
    })
    muutokset(rivi.hetu, value => {
      osakas.hetuTaiYtunnus = this._stringService.normalizeDash(value) || null
    })
    muutokset(rivi.osakkeita, value => {
      osakas.osakkeita = value || null
    })
    muutokset(rivi.kansallisuus, value => {
      osakas.kansallisuus = value || null
    })
    muutokset(rivi.pep, value => {
      osakas.pep = value ?? null
    })
    muutokset(rivi.noFinnishId, value => {
      osakas.noFinnishId = value ?? false
      ilmoitaUudelleen(rivi.hetu)
    })

    muutokset(rivi.birthDate, value => {
      if (value && rivi.birthDate().valid()) {
        osakas.birthDate = this._dateService.dateToLocalDate(value)
      }
    })

    muutokset(rivi.onkoYritys, value => {
      osakas.onkoYritys = value ?? null
    })

    // Note! Do not change order of these setValues
    asetaArvo(rivi.onkoYritys, osakas.onkoYritys ?? null)
    asetaArvo(rivi.noFinnishId, osakas.noFinnishId ?? null)

    this._rivienTilaukset.osakkaat.push(tilaukset)

    if (!firestoresta) {
      this.tunnistamistiedot.osakkaat.push(osakas)
    }
  }

  poistaOsakas(index: number) {
    this._poistaRivi(this.lomake.osakkaat, this._rivienTilaukset.osakkaat, index)
    this.tunnistamistiedot.osakkaat.splice(index, 1)
  }
  lisaaUusiHallituksenJasen(firestoresta?: CustomerHallituksenJasen) {
    const jasen: CustomerHallituksenJasen = firestoresta
      ? firestoresta
      : {
          hetuTaiYtunnus: '',
          nimi: '',
          kansallisuus: null,
          pep: null,
          noFinnishId: null,
          birthDate: null,
          rooli: null,
        }

    const rivit = this.lomake.hallitus().value()
    this._asetaRivit(this.lomake.hallitus, [
      ...rivit,
      {
        nimi: jasen.nimi ?? '',
        hetu: jasen.hetuTaiYtunnus ?? '',
        kansallisuus: jasen.kansallisuus ?? null,
        pep: jasen.pep ?? null,
        noFinnishId: null,
        birthDate: jasen.birthDate ? this._dateService.localDateToDate(jasen.birthDate) : null,
        rooli: jasen.rooli ?? null,
      },
    ])
    this._hallitusPoissa.set(false)
    const rivi = this.lomake.hallitus[rivit.length]
    const tilaukset = new Subscription()
    const muutokset = this._muutokset(tilaukset)

    muutokset(rivi.nimi, value => {
      jasen.nimi = value || null
    })
    muutokset(rivi.hetu, value => {
      jasen.hetuTaiYtunnus = this._stringService.normalizeDash(value) || null
    })
    muutokset(rivi.kansallisuus, value => {
      jasen.kansallisuus = value || null
    })
    muutokset(rivi.pep, value => {
      jasen.pep = value ?? null
    })
    muutokset(rivi.noFinnishId, value => {
      jasen.noFinnishId = value ?? false
      ilmoitaUudelleen(rivi.hetu)
    })

    muutokset(rivi.birthDate, value => {
      if (value && rivi.birthDate().valid()) {
        jasen.birthDate = this._dateService.dateToLocalDate(value)
      }
    })

    muutokset(rivi.rooli, value => {
      jasen.rooli = value || null
    })

    asetaArvo(rivi.noFinnishId, jasen.noFinnishId ?? null)
    this._rivienTilaukset.hallitus.push(tilaukset)

    if (!firestoresta) {
      this.tunnistamistiedot.hallitus.push(jasen)
    }
  }

  poistaHallituksenJasen(index: number) {
    this._poistaRivi(this.lomake.hallitus, this._rivienTilaukset.hallitus, index)
    this.tunnistamistiedot.hallitus.splice(index, 1)
  }

  lisaaUusiEdunsaaja(firestoresta?: CustomerBeneficiary) {
    const saaja: CustomerBeneficiary = firestoresta
      ? firestoresta
      : {
          hetuTaiYtunnus: '',
          nimi: '',
          kansallisuus: null,
          pep: null,
        }

    const rivit = this.lomake.edunsaajat().value()
    this._asetaRivit(this.lomake.edunsaajat, [
      ...rivit,
      {
        nimi: saaja.nimi ?? '',
        hetu: saaja.hetuTaiYtunnus ?? '',
        kansallisuus: saaja.kansallisuus ?? null,
        pep: saaja.pep ?? null,
        noFinnishId: null,
        birthDate: saaja.birthDate ? this._dateService.localDateToDate(saaja.birthDate) : null,
      },
    ])
    this._edunsaajatPoissa.set(false)
    const rivi = this.lomake.edunsaajat[rivit.length]
    const tilaukset = new Subscription()
    const muutokset = this._muutokset(tilaukset)

    muutokset(rivi.nimi, value => {
      saaja.nimi = value || null
    })
    muutokset(rivi.hetu, value => {
      saaja.hetuTaiYtunnus = this._stringService.normalizeDash(value) || null
    })
    muutokset(rivi.kansallisuus, value => {
      saaja.kansallisuus = value || null
    })
    muutokset(rivi.pep, value => {
      saaja.pep = value ?? null
    })
    muutokset(rivi.noFinnishId, value => {
      saaja.noFinnishId = value ?? false
      ilmoitaUudelleen(rivi.hetu)
    })

    muutokset(rivi.birthDate, value => {
      if (value && rivi.birthDate().valid()) {
        saaja.birthDate = this._dateService.dateToLocalDate(value)
      }
    })

    asetaArvo(rivi.noFinnishId, saaja.noFinnishId ?? null)

    this._rivienTilaukset.edunsaajat.push(tilaukset)
    if (!this.tunnistamistiedot.edunsaajat) {
      this.tunnistamistiedot.edunsaajat = []
    }
    if (!firestoresta) {
      this.tunnistamistiedot.edunsaajat.push(saaja)
    }
  }

  poistaEdunsaaja(index: number) {
    this._poistaRivi(this.lomake.edunsaajat, this._rivienTilaukset.edunsaajat, index)
    this.tunnistamistiedot.edunsaajat.splice(index, 1)
  }

  async tallenna() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    // Show loading indicator
    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = this._asiakasSubject.value

      const batch = this._firebaseLemonaid.firestoreBatch()

      // Remove unnecessary data (birthDate & onkoYritys fields are disabled when the osakas has a Finnish hetu/y-tunnus)
      for (const osakas of this.tunnistamistiedot.osakkaat) {
        if (!osakas.noFinnishId) {
          osakas.birthDate = null
          osakas.onkoYritys = null
        }
      }

      const now = this._timestampService.now()
      this.tunnistamistiedot.asiakasAvain = asiakas.avain
      this.tunnistamistiedot.avain = asiakas.avain
      this.tunnistamistiedot.luotu = this.tunnistamistiedot.luotu || now
      this.tunnistamistiedot.paivitetty = now

      const kirjanpitajaTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      this.tunnistamistiedot.luoja = this.tunnistamistiedot.luoja || kirjanpitajaTiedot.uid
      this.tunnistamistiedot.paivittaja = kirjanpitajaTiedot.uid

      // Save KYC data
      const saveKycDoc = this._kycUriService.getCustomerKycDocUri(asiakas.avain)
      batch.set(saveKycDoc, this.tunnistamistiedot)

      // Create history entry
      const historyCopy = this._copyService.cloneObjectDeep(this.tunnistamistiedot)
      const historyAvain = this._firebaseLemonaid.firestoreCreateId()
      const kycHistoryDoc = this._kycUriService.getCustomerKycHistoryDocUri(asiakas.avain, historyAvain)
      historyCopy.avain = historyAvain
      batch.set(kycHistoryDoc, historyCopy)

      // Commit batch
      await batch.commit()

      // Create work queue for KYC -> TilinpaatosUser conversion
      const conversionData: TilinpaatosUserConversionWorkQueue = {
        asiakasAvain: asiakas.avain,
        startedAt: this._timestampService.now(),
      }
      await this._firebase.firestoreSetData('tyojono/' + asiakas.avain + '/tilinpaatos-user-conversion/' + this._firebase.firestoreCreateId(), conversionData)

      // Stop loading indicator
      this._ladataanService.lopetaLataaminen()

      // All is good, let's advance
      this._snackbar.open('Tallennus onnistui', 'OK', { duration: 5000, verticalPosition: 'top' })
    } catch (error) {
      this._ladataanService.lopetaLataaminen()
      this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli)
      this._errorHandler.handleError(error)
    }
  }

  private _alustaKycLomakkeenTiedot(kyc: KnowYourCustomer) {
    this._setEmptyForm()
    this._lomakePoissa.set(false)

    for (const osakas of kyc.osakkaat) {
      this.lisaaUusiOsakas(osakas)
    }

    for (const hallituksenJasen of kyc.hallitus) {
      this.lisaaUusiHallituksenJasen(hallituksenJasen)
    }
    if (kyc.edunsaajat) {
      for (const edunsaaja of kyc.edunsaajat) {
        this.lisaaUusiEdunsaaja(edunsaaja)
      }
    }
  }
  private _createBlankKyc(): KnowYourCustomer {
    const blank: KnowYourCustomer = {
      asiakasAvain: '',
      osakkaat: [],
      hallitus: [],
      edunsaajat: [],
      avain: '',
      paivitetty: null,
      paivittaja: '',
      luotu: null,
      luoja: '',
      poistettu: false,
    }
    return blank
  }

  private _setEmptyForm() {
    for (const tilaukset of [...this._rivienTilaukset.osakkaat, ...this._rivienTilaukset.hallitus, ...this._rivienTilaukset.edunsaajat]) {
      tilaukset.unsubscribe()
    }
    this._rivienTilaukset.osakkaat = []
    this._rivienTilaukset.hallitus = []
    this._rivienTilaukset.edunsaajat = []
    this._asetaRivit(this.lomake.osakkaat, [])
    this._asetaRivit(this.lomake.hallitus, [])
    this._asetaRivit(this.lomake.edunsaajat, [])
  }

  private _poistaRivi<T>(taulukko: FieldTree<T[]>, tilaukset: Subscription[], index: number) {
    tilaukset[index]?.unsubscribe()
    tilaukset.splice(index, 1)
    this._asetaRivit(
      taulukko,
      taulukko()
        .value()
        .filter((_, i) => i !== index),
    )
  }

  // Reaktiivisen lomakkeen disable() lähetti jokaisen kentän arvon tilaajille, jotka normalisoivat näytettävän historiaversion tietoja.
  // Tyhjä taulukko jäi käytöstä poistetuksi, kunnes siihen lisättiin rivi.
  private _poistaLomakeKaytosta() {
    this._lomakePoissa.set(true)
    for (const rivi of this.lomake.osakkaat) {
      ilmoitaUudelleen(rivi.nimi)
      ilmoitaUudelleen(rivi.hetu)
      ilmoitaUudelleen(rivi.osakkeita)
      ilmoitaUudelleen(rivi.kansallisuus)
      ilmoitaUudelleen(rivi.pep)
      ilmoitaUudelleen(rivi.noFinnishId)
      ilmoitaUudelleen(rivi.onkoYritys)
    }
    for (const rivi of this.lomake.hallitus) {
      ilmoitaUudelleen(rivi.nimi)
      ilmoitaUudelleen(rivi.hetu)
      ilmoitaUudelleen(rivi.kansallisuus)
      ilmoitaUudelleen(rivi.pep)
      ilmoitaUudelleen(rivi.noFinnishId)
      ilmoitaUudelleen(rivi.rooli)
    }
    for (const rivi of this.lomake.edunsaajat) {
      ilmoitaUudelleen(rivi.nimi)
      ilmoitaUudelleen(rivi.hetu)
      ilmoitaUudelleen(rivi.kansallisuus)
      ilmoitaUudelleen(rivi.pep)
      ilmoitaUudelleen(rivi.noFinnishId)
    }
    if (!this.lomake.osakkaat().value().length) {
      this._osakkaatPoissa.set(true)
    }
    if (!this.lomake.hallitus().value().length) {
      this._hallitusPoissa.set(true)
    }
    if (!this.lomake.edunsaajat().value().length) {
      this._edunsaajatPoissa.set(true)
    }
  }

  async vaadiTuntemistietojenPaivitysta() {
    const data: AreYouSureDialogData = {
      header: 'Oletko varma?',
      rightAction: 'Kyllä',
      leftAction: 'Ei',
      text: 'Tämä aktivoi tuntemistietojen antamisen ponnahdusikkunan asiakkaan Lemonaid-tilillä.',
    }

    const dialogHandle = this._dialog.open(AreYouSureDialog, { data: data })
    dialogHandle
      .afterClosed()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(yes => {
        if (yes) {
          const asiakas = this._asiakasSubject.value
          asiakas.tuntemistietojenStatus = 'vaatii-paivityksen'

          this._ladataanService.aloitaLataaminen()

          return this._asiakasService.paivitaAsiakas(asiakas, 'tilikaudet-eivat-voineet-muuttua', false, false).finally(() => {
            this._ladataanService.lopetaLataaminen()
          })
        }
      })
  }

  async selectDisplayedHistory(direction: 'prev' | 'next' | 'init') {
    const asiakas = this._asiakasSubject.value
    const historiaCollection = this._kycUriService.getCustomerKycHistoryCollection(asiakas.avain)
    let historiaQuery = this._firebaseLemonaid.firestoreCollection<KnowYourCustomer>(historiaCollection)
    const currentLatestDate = this.selectedHistorySubject.value?.paivitetty || this._timestampService.now()

    if (direction === 'prev') {
      historiaQuery = historiaQuery.orderBy('paivitetty', 'desc').startAfter(currentLatestDate).limit(2)
    } else if (direction === 'next') {
      historiaQuery = historiaQuery.orderBy('paivitetty', 'asc').startAfter(currentLatestDate).limit(2)
    } else {
      historiaQuery = historiaQuery.orderBy('paivitetty', 'desc').limit(2)
    }
    const historia: KnowYourCustomer = await historiaQuery
      .get()
      .then(resp => {
        if (!resp?.length) {
          return null
        }
        this.isBrowsingHistory = true

        const curr = resp?.[0]
        let prev = null

        if (direction === 'next') {
          const next = resp?.[1]
          this.nextActive = !!next
          prev = this.selectedHistorySubject.value
        } else {
          prev = resp?.[1]
        }

        this.prevActive = !!prev

        // Reset values
        this.historyChangesMap.clear()
        this._findDeletionsInHistory(curr, prev)
        this._mapHistoryChanges(curr, prev)
        return curr
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        return null
      })
    if (historia) {
      this.selectedHistorySubject.next(historia)
    }
    this._ladataanService.lopetaLataaminen()
  }
  resetDisplayedHistory() {
    this.selectedHistorySubject.next(null)
  }

  private _mapHistoryChanges(curr: KnowYourCustomer, prev: KnowYourCustomer) {
    if (!prev) {
      return
    }
    // Compare osakkaat
    for (const [idx, currOsakas] of (curr?.osakkaat || []).entries()) {
      const prevOsakas =
        prev.osakkaat?.find(osakas => osakas.hetuTaiYtunnus?.trim() === currOsakas.hetuTaiYtunnus?.trim() || osakas.nimi?.toLowerCase() === currOsakas.nimi?.trim().toLowerCase()) ||
        prev.osakkaat?.[idx]
      if (!prevOsakas) {
        this._setAllFieldsChangedInMap(idx, 'o')
        continue
      }
      this._compareHistoriesAddToMapIfNeeded(idx, 'o', currOsakas, prevOsakas)
    }

    // Compare hallitus
    for (const [idx, currJasen] of (curr.hallitus || []).entries()) {
      const prevJasen =
        prev.hallitus?.find(jasen => jasen.hetuTaiYtunnus?.trim() === currJasen.hetuTaiYtunnus?.trim() || jasen.nimi?.toLowerCase() === currJasen.nimi?.trim().toLowerCase()) || prev.hallitus?.[idx]
      if (!prevJasen) {
        this._setAllFieldsChangedInMap(idx, 'h')
        continue
      }
      this._compareHistoriesAddToMapIfNeeded(idx, 'h', currJasen, prevJasen)
    }

    // Compare edunsaajat
    for (const [idx, currEdunsaaja] of (curr.edunsaajat || []).entries()) {
      const prevEdunsaaja =
        prev.edunsaajat?.find(edunsaaja => edunsaaja.hetuTaiYtunnus?.trim() === currEdunsaaja.hetuTaiYtunnus?.trim() || edunsaaja.nimi?.toLowerCase() === currEdunsaaja.nimi?.trim().toLowerCase()) ||
        prev.edunsaajat?.[idx]
      if (!prevEdunsaaja) {
        this._setAllFieldsChangedInMap(idx, 'e')
        continue
      }
      this._compareHistoriesAddToMapIfNeeded(idx, 'e', currEdunsaaja, prevEdunsaaja)
    }
  }

  private _setAllFieldsChangedInMap(idx: number, osakasHallitusOrEdunsaaja: 'o' | 'h' | 'e') {
    this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'hetuTaiYtunnus', 'changed')
    this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'nimi', 'changed')
    this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'pep', 'changed')
    this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'kansallisuus', 'changed')

    if (osakasHallitusOrEdunsaaja === 'o') {
      this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'osakkeita', 'changed')
    }
    if (osakasHallitusOrEdunsaaja === 'h') {
      this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'rooli', 'changed')
    }
  }

  private _compareHistoriesAddToMapIfNeeded(
    idx: number,
    osakasHallitusOrEdunsaaja: 'o' | 'h' | 'e',
    curr: CustomerOsakas | CustomerHallituksenJasen | CustomerBeneficiary,
    prev: CustomerOsakas | CustomerHallituksenJasen | CustomerBeneficiary,
  ) {
    if (curr.hetuTaiYtunnus?.trim().toLowerCase() !== prev.hetuTaiYtunnus?.trim().toLowerCase()) {
      this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'hetuTaiYtunnus', 'changed')
    }
    if (curr.nimi?.trim().toLowerCase() !== prev.nimi?.trim().toLowerCase()) {
      this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'nimi', 'changed')
    }
    if (curr.kansallisuus !== prev.kansallisuus) {
      this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'kansallisuus', 'changed')
    }
    if (curr.pep !== prev.pep) {
      this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'pep', 'changed')
    }
    if (osakasHallitusOrEdunsaaja === 'o') {
      if ((curr as CustomerOsakas).osakkeita !== (prev as CustomerOsakas).osakkeita) {
        this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'osakkeita', 'changed')
      }
    }
    if (osakasHallitusOrEdunsaaja === 'h') {
      if ((curr as CustomerHallituksenJasen).rooli !== (prev as CustomerHallituksenJasen).rooli) {
        this.historyChangesMap.set(idx + osakasHallitusOrEdunsaaja + 'rooli', 'changed')
      }
    }
  }

  private _findDeletionsInHistory(curr: KnowYourCustomer, prev: KnowYourCustomer) {
    // Reset values
    this.deletedOsakkaatText = null
    this.deletedHallitusText = null
    this.deletedEdunsaajatText = null

    const deletedOsakkaat = this._compareHistoriesForDeletions(curr.osakkaat, prev?.osakkaat)
    if (deletedOsakkaat.length) {
      this.deletedOsakkaatText = 'Poistettiin: ' + deletedOsakkaat.map(d => d.nimi + ' (' + d.hetuTaiYtunnus + ')').join(', ') + '.'
    }

    const deletedHallitus = this._compareHistoriesForDeletions(curr.hallitus, prev?.hallitus)
    if (deletedHallitus.length) {
      this.deletedHallitusText = 'Poistettiin: ' + deletedHallitus.map(d => d.nimi + ' (' + d.hetuTaiYtunnus + ')').join(', ') + '.'
    }

    const deletedEdunsaajat = this._compareHistoriesForDeletions(curr.edunsaajat, prev?.edunsaajat)
    if (deletedEdunsaajat.length) {
      this.deletedEdunsaajatText = 'Poistettiin: ' + deletedEdunsaajat.map(d => d.nimi + ' (' + d.hetuTaiYtunnus + ')').join(', ') + '.'
    }
  }

  private _compareHistoriesForDeletions(curr: CustomerOsakas[] | CustomerBeneficiary[] | CustomerHallituksenJasen[], prev: CustomerOsakas[] | CustomerBeneficiary[] | CustomerHallituksenJasen[]) {
    if (curr.length >= (prev?.length ?? 0)) {
      // There are more items in the current array than in the previous one, meaning that nothing was deleted.
      return []
    }
    const deletedSincePrev: any[] = []

    // Some osakkaat were deleted
    for (const p of prev || []) {
      const isDeleted = curr?.findIndex(c => p.hetuTaiYtunnus?.trim() === c.hetuTaiYtunnus?.trim() || p.nimi?.toLowerCase() === c.nimi?.trim().toLowerCase()) === -1
      if (isDeleted) {
        deletedSincePrev.push(p)
      }
    }
    return deletedSincePrev
  }

  editForm() {
    this.isBrowsingHistory = false
    this.selectedHistorySubject.next(null)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
